import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Signup({ onSuccess, onClose, industries, onShowLogin }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    industry: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      // Send only required fields (exclude confirmPassword)
      const { confirmPassword, ...signupData } = formData;
      const response = await axios.post(`${API}/users`, signupData);
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div 
        className="max-w-md w-full rounded-2xl shadow-2xl p-8 fade-in max-h-[90vh] overflow-y-auto" 
        style={{
          background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.15) 0%, rgba(17, 17, 17, 0.95) 50%, rgba(139, 92, 246, 0.1) 100%)',
          border: '2px solid rgba(139, 92, 246, 0.4)',
          boxShadow: '0 0 40px rgba(139, 92, 246, 0.2), inset 0 0 60px rgba(139, 92, 246, 0.05)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold" style={{
            background: 'linear-gradient(135deg, #8B5CF6, #A78BFA, #C4B5FD)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Get Started</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl transition"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Name
            </label>
            <input
              type="text"
              data-testid="signup-name-input"
              required
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter your name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              data-testid="signup-email-input"
              required
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              data-testid="signup-password-input"
              required
              minLength={6}
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Create a password (min 6 characters)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              data-testid="signup-confirm-password-input"
              required
              minLength={6}
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              placeholder="Confirm your password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Select Your Industry
            </label>
            <select
              data-testid="signup-industry-select"
              required
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            >
              <option value="" className="bg-gray-900">Choose an industry...</option>
              {industries.map((industry) => (
                <option key={industry} value={industry} className="bg-gray-900">
                  {industry}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg" data-testid="signup-error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="signup-submit-button"
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            style={{
              background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 50%, #A78BFA 100%)',
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
            }}
            onMouseOver={(e) => e.target.style.boxShadow = '0 0 30px rgba(139, 92, 246, 0.6)'}
            onMouseOut={(e) => e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.4)'}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating Account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="text-center text-gray-400 text-sm mt-4">
          Already have an account?{' '}
          <button 
            onClick={onShowLogin}
            className="font-medium hover:underline"
            style={{ color: '#A78BFA' }}
          >
            Login
          </button>
        </p>
      </div>
    </div>
  );
}

export default Signup;
